<template>
  <div :class="attrs.class">
    <FormLabel
      v-if="slots.label"
      :tooltip="tooltip"
      :is-required="isRequired"
      :id="id"
      class="mb-2 dark:text-white"
    >
      <slot name="label" />
    </FormLabel>
    <div class="flex space-x-2">
      <textarea
        ref="inputRef"
        v-if="as === 'textarea'"
        v-bind="sharedAttributes"
        @input="handleInputChange"
      />
      <input
        ref="inputRef"
        v-else
        v-bind="sharedAttributes"
        @input="handleInputChange"
      />
      <slot name="input-append" />
    </div>
    <div
      v-if="hasErrors || isCharacterCounterShown || isCaptionShown"
      class="mt-2 space-y-1"
    >
      <FormErrors v-if="hasErrors" :id="id" :errors="errors" />
      <FormCaption
        v-if="isCharacterCounterShown"
        :class="isOverMaxCharacters ? 'text-error-500' : ''"
      >
        {{ modelValue ? modelValue.length : 0 }}
        <span v-if="maxCharacters > 0">/ {{ maxCharacters }}</span>
        characters
        <span v-if="minCharacters > 0">(min. {{ minCharacters }})</span>
      </FormCaption>
      <FormCaption v-if="isCaptionShown">
        <slot name="caption" />
      </FormCaption>
    </div>
  </div>
</template>

<script setup>
import FormLabel from './FormLabel.vue'
import FormErrors from './FormErrors.vue'
import FormCaption from './FormCaption.vue'
import { computed, useAttrs, useSlots, useTemplateRef, useId } from 'vue'
import { twMerge } from 'tailwind-merge'

const inputRef = useTemplateRef('inputRef')

defineExpose({
  inputRef,
})

const emit = defineEmits(['update:modelValue', 'input'])

const props = defineProps({
  as: {
    type: String,
    default: 'input',
  },
  modelValue: {
    type: [String, Number],
    default: '',
  },
  isError: {
    type: Boolean,
    default: false,
  },
  errors: {
    type: [Array, String],
    default: '',
  },
  inputClasses: {
    type: [Array, String, Object],
    default: () => [],
  },
  isRequired: {
    type: Boolean,
    default: false,
  },
  tooltip: {
    type: String,
    default: '',
  },
  minCharacters: {
    type: Number,
    default: 0,
  },
  maxCharacters: {
    type: Number,
    default: 0,
  },
})

const attrs = useAttrs()
const slots = useSlots()

const id = `growthmentor-input-${useId()}`

const errorClasses = computed(() => {
  return (
    props.isError &&
    'bg-white border-error-400 text-error-500 placeholder:text-error-500 focus:ring-0 focus:border-error-400 disabled:text-error-500'
  )
})
const hasErrors = computed(() => {
  if (!props.isError) return false
  const isString = typeof props.errors === 'string'
  return isString ? props.errors : props.errors.length
})
const isCharacterCounterShown = computed(() => {
  return props.minCharacters > 0 || props.maxCharacters > 0
})
const isOverMaxCharacters = computed(() => {
  if (props.maxCharacters <= 0) return false
  return (props.modelValue?.length ?? 0) > props.maxCharacters
})
const isCaptionShown = computed(() => {
  return slots.caption
})
const sharedAttributes = computed(() => {
  const normalizedInputClasses = Array.isArray(props.inputClasses)
    ? props.inputClasses
    : [props.inputClasses]

  return {
    ...attrs,
    class: twMerge(
      'focus-visible:border-primary-400 focus-visible:ring-primary-200 block w-full rounded-xl border-neutral-200 bg-neutral-50 px-4 py-3 text-neutral-700 placeholder:text-sm placeholder:text-neutral-600 focus:ring-0 focus:outline-none focus-visible:bg-white focus-visible:text-neutral-700 focus-visible:ring-2 disabled:bg-neutral-100 disabled:text-neutral-400 dark:border-neutral-700 dark:bg-neutral-700 dark:text-white dark:placeholder:text-neutral-400 dark:focus-visible:border-neutral-800 dark:focus-visible:bg-neutral-700 dark:focus-visible:text-white dark:focus-visible:ring-neutral-800',
      'relative shadow-sm',
      errorClasses.value,
      ...normalizedInputClasses,
    ),
    'aria-invalid': props.isError,
    'aria-describedby': id,
    'aria-readonly': Object.keys(attrs).includes('readonly').toString(),
    'aria-disabled': Object.keys(attrs).includes('disabled').toString(),
    'aria-required': props.isRequired && props.isRequired.toString(),
    id,
    value: props.modelValue,
    test_id: attrs.test_id,
  }
})

const handleInputChange = (event) => {
  const { min, max, value, type } = event.target
  // Compare as numbers. The attributes are strings, so "9" > "69" was true
  // and a single digit typed into a field with max="69" became 69.
  if (value && type === 'number') {
    if (min !== '' && Number(value) < Number(min)) {
      event.target.value = min
    } else if (max !== '' && Number(value) > Number(max)) {
      event.target.value = max
    }
  }
  emit('update:modelValue', event.target.value)
  // 'input' emit is backward compatibility with Vue 2 v-model
  emit('input', event.target.value)
}
</script>

<script>
export default {
  name: 'FormInput',
  inheritAttrs: false,
}
</script>
